import { createRef, useContext, useRef, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  IPersonEfteruddannelseFilterSetting,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import {
  EfteruddannelseIndberetningStatusId,
  PersonDtoFilterInput,
} from "../../../../interfaces/schema";
import InputCheckbox from "../../../formcontrols/InputCheckbox";
import { random, startCase } from "lodash";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../select/Select";
import {
  dateQueries,
  numberQueries,
} from "../../../../config/filter/CustomFilterConfig";
import { ComboBox } from "../../../filter/ComboBox";
import { DatePicker, useToaster, Notification } from "rsuite";
import { isCompleteDate } from "../../../formcontrols/datePickerValue";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { format } from "date-fns";
import { f } from "../../../../util/f";
import WarningIcon from "../../../icons/WarningIcon";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "efteruddannelse";

export default function PersonEfteruddannelsesFilter() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find active settings
  const settings: IPersonEfteruddannelseFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // react thigns
  // reffys
  const checkboxRefs = useRef(
    [...Array(Object.keys(EfteruddannelseIndberetningStatusId).length)].map(
      () => createRef<HTMLInputElement>(),
    ),
  );

  // refs
  const inputRef = useRef<HTMLInputElement>(null);
  const inputToRef = useRef<HTMLInputElement>(null);

  // state stuff
  const [selectedEndDateQuery, setSelectedEndDateQuery] = useState(
    dateQueries[0],
  );
  const [selectedLektionerQuery, setSelectedLektionerQuery] = useState(
    numberQueries[0],
  );
  const [selectedReceivedDateQuery, setSelectedReceivedDateQuery] = useState(
    dateQueries[0],
  );

  const [isLektionerBetween, setIsLektionerBetween] = useState<boolean>(false);
  const [isEndDateBetween, setIsEndDateBetween] = useState<boolean>(false);
  const [isReceivedDateBetween, setIsReceivedDateBetween] =
    useState<boolean>(false);

  const [selectedEndDateFrom, setSelectedEndDateFrom] = useState<Date | null>(
    new Date(),
  );
  const [selectedEndDateTo, setSelectedEndDateTo] = useState<Date | null>(
    new Date(),
  );

  const [selectedReceivedDateFrom, setSelectedReceivedDateFrom] =
    useState<Date | null>(new Date());
  const [selectedReceivedDateTo, setSelectedReceivedDateTo] =
    useState<Date | null>(new Date());

  // for all your toasting purposes
  const toaster = useToaster();

  // helper function
  function getAndConvertStatusValues() {
    // get checkbox status things
    // get all refs
    const refValues = checkboxRefs.current.flatMap((r) => {
      if (r.current) {
        if (r.current.checked) {
          return r.current?.id;
        } else {
          return [];
        }
      } else {
        return [];
      }
    });

    // remove id's from values
    const statusValues: Array<EfteruddannelseIndberetningStatusId> =
      refValues.map(
        (val) =>
          val.substring(
            0,
            val.indexOf("__"),
          ) as EfteruddannelseIndberetningStatusId,
      );

    return statusValues;
  }

  function buildFilter(updatedSettings: IPersonEfteruddannelseFilterSetting) {
    const filter = f<PersonDtoFilterInput>()
      .addFilter("personEfteruddannelse", {
        some: {
          and: [
            // status
            updatedSettings.status.length
              ? {
                  personEfteruddannelseIndberetning: {
                    some: {
                      efteruddannelseIndberetningStatus: {
                        in: updatedSettings.status,
                      },
                    },
                  },
                }
              : {},

            // lektioner
            updatedSettings.antalLektioner.length
              ? {
                  or: updatedSettings.antalLektioner.map((item) => item.filter),
                }
              : {},

            // modtaget/registrerings datoer
            updatedSettings.modtagetDatoer.length
              ? {
                  or: [
                    {
                      personEfteruddannelseIndberetning: {
                        some: {
                          or: updatedSettings.modtagetDatoer.map(
                            (item) => item.filter,
                          ),
                        },
                      },
                    },
                  ],
                }
              : {},

            // slut dato
            updatedSettings.slutDatoer.length
              ? {
                  periode: {
                    or: updatedSettings.slutDatoer.map((item) => item.filter),
                  },
                }
              : {},
          ],
        },
      })
      .build();
    return filter;
  }

  // handlers
  function updateFilterHandler(
    eventType: "status" | "lessons" | "receivedDate" | "endDate",
  ) {
    // get checkbox status things
    const statusValues = getAndConvertStatusValues();

    // do antal lektioner things
    const updatedLektionerSettings = settings?.antalLektioner || [];

    if (eventType === "lessons") {
      let lektionerItem:
        | IPersonEfteruddannelseFilterSetting["antalLektioner"][number]
        | undefined;

      if (isLektionerBetween) {
        const inputFromValue = inputRef.current?.value;
        const inputToValue = inputToRef.current?.value;

        if (inputFromValue && inputToValue) {
          lektionerItem = {
            key: `gt-${parseInt(inputFromValue)}-lt-${parseInt(inputToValue)}`,
            selectedQueryName: "Mellem",
            displayName: `${parseInt(inputFromValue)} - ${parseInt(
              inputToValue,
            )}`,
            filter: {
              and: [
                { lektionerGennemfoert: { gt: parseInt(inputFromValue) } },
                { lektionerGennemfoert: { lt: parseInt(inputToValue) } },
              ],
            },
          };
        }
      } else {
        const inputValue = inputRef.current?.value;
        if (inputValue) {
          lektionerItem = {
            key: `${selectedLektionerQuery.query}-${parseInt(inputValue)}`,
            selectedQueryName: selectedLektionerQuery.displayName,
            displayName: inputValue,
            filter: {
              lektionerGennemfoert: {
                [selectedLektionerQuery.query]: parseInt(inputValue),
              },
            },
          };
        }
      }

      if (lektionerItem) {
        if (
          !settings?.antalLektioner.some(
            (item) => item.key === lektionerItem?.key,
          )
        ) {
          updatedLektionerSettings.push(lektionerItem);
        } else {
          toaster.push(
            <Notification>
              <div className="d-flex flex-row gap-3 align-items-center">
                <span className={`c-theme-icon text-light bg-warning`}>
                  <WarningIcon />
                </span>
                Filteret er allerede tilføjet.
              </div>
            </Notification>,
            {
              placement: "bottomEnd",
              duration: 2500,
            },
          );
        }
      }
    }

    // do modtaget dato things
    const updatedRegDatoSettings = settings?.modtagetDatoer || [];

    if (eventType === "receivedDate") {
      // OK is disabled while the date is unfinished; the to-date is only
      // read when "between" is chosen (ADV-320).
      if (!selectedReceivedDateFrom) return;
      const selectedReceivedDateFromFormattedPretty = format(
        selectedReceivedDateFrom,
        "dd-MM-yyyy",
      );
      const selectedReceivedDateFromFormattedFilter = format(
        selectedReceivedDateFrom,
        "yyyy-MM-dd",
      );
      const selectedReceivedDateToFormattedPretty = format(
        selectedReceivedDateTo ?? selectedReceivedDateFrom,
        "dd-MM-yyyy",
      );
      const selectedReceivedDateToFormattedFilter = format(
        selectedReceivedDateTo ?? selectedReceivedDateFrom,
        "yyyy-MM-dd",
      );

      let receivedDateItem: IPersonEfteruddannelseFilterSetting["modtagetDatoer"][number];

      if (isReceivedDateBetween) {
        receivedDateItem = {
          key: `gt-${selectedReceivedDateFromFormattedPretty}-lt-${selectedReceivedDateToFormattedPretty}`,
          displayName: `${selectedReceivedDateFromFormattedPretty} - ${selectedReceivedDateToFormattedPretty}`,
          selectedQueryName: "Mellem",
          filter: {
            and: [
              {
                registreringsDato: {
                  gt: selectedReceivedDateFromFormattedFilter,
                },
              },
              {
                registreringsDato: {
                  lt: selectedReceivedDateToFormattedFilter,
                },
              },
            ],
          },
        };
      } else {
        receivedDateItem = {
          key: `${selectedReceivedDateQuery.query}-${selectedReceivedDateFromFormattedPretty}`,
          displayName: selectedReceivedDateFromFormattedPretty,
          selectedQueryName: selectedReceivedDateQuery.displayName,
          filter: {
            registreringsDato: {
              [selectedReceivedDateQuery.query]:
                selectedReceivedDateFromFormattedFilter,
            },
          },
        };
      }

      if (
        !updatedRegDatoSettings.some(
          (item) => item.key === receivedDateItem.key,
        )
      ) {
        updatedRegDatoSettings.push(receivedDateItem);
      } else {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon text-light bg-warning`}>
                <WarningIcon />
              </span>
              Filteret er allerede tilføjet.
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 2500,
          },
        );
      }
    }

    const updatedEndDateSettings = settings?.slutDatoer || [];

    if (eventType === "endDate") {
      // OK is disabled while the date is unfinished; the to-date is only
      // read when "between" is chosen (ADV-320).
      if (!selectedEndDateFrom) return;
      const selectedEndDateFromFormattedPretty = format(
        selectedEndDateFrom,
        "dd-MM-yyyy",
      );
      const selectedEndDateFromFormattedFilter = format(
        selectedEndDateFrom,
        "yyyy-MM-dd",
      );
      const selectedEndDateToFormattedPretty = format(
        selectedEndDateTo ?? selectedEndDateFrom,
        "dd-MM-yyyy",
      );
      const selectedEndDateToFormattedFilter = format(
        selectedEndDateTo ?? selectedEndDateFrom,
        "yyyy-MM-dd",
      );

      let endDateItem: IPersonEfteruddannelseFilterSetting["slutDatoer"][number];

      if (isEndDateBetween) {
        endDateItem = {
          key: `gt-${selectedEndDateFromFormattedPretty}-lt-${selectedEndDateToFormattedPretty}`,
          displayName: `${selectedEndDateFromFormattedPretty} - ${selectedEndDateToFormattedPretty}`,
          selectedQueryName: "Mellem",
          filter: {
            and: [
              {
                tilDatoInklusiv: {
                  gt: selectedEndDateFromFormattedFilter,
                },
              },
              {
                tilDatoInklusiv: {
                  lt: selectedEndDateToFormattedFilter,
                },
              },
            ],
          },
        };
      } else {
        endDateItem = {
          key: `${selectedEndDateQuery.query}-${selectedEndDateFromFormattedPretty}`,
          displayName: selectedEndDateFromFormattedPretty,
          selectedQueryName: selectedEndDateQuery.displayName,
          filter: {
            tilDatoInklusiv: {
              [selectedEndDateQuery.query]: selectedEndDateFromFormattedFilter,
            },
          },
        };
      }

      if (
        !updatedEndDateSettings.some((item) => item.key === endDateItem.key)
      ) {
        updatedEndDateSettings.push(endDateItem);
      } else {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon text-light bg-warning`}>
                <WarningIcon />
              </span>
              Filteret er allerede tilføjet.
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 2500,
          },
        );
      }
    }

    const updatedSettings: IPersonEfteruddannelseFilterSetting = {
      status: statusValues,
      antalLektioner: updatedLektionerSettings,
      modtagetDatoer: updatedRegDatoSettings,
      slutDatoer: updatedEndDateSettings,
    };

    const canBuildFilter =
      updatedSettings.antalLektioner.length ||
      updatedSettings.modtagetDatoer.length ||
      updatedSettings.slutDatoer.length ||
      updatedSettings.status.length;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    // update the query filter
    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonEfteruddannelseFilterSetting
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    if (queryFilter.filter === "") {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    } else {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    }
  }

  function removeFilterHandler(
    key: string,
    eventType: "lektioner" | "modtaget" | "slutDato",
  ) {
    // get checkbox status things
    const statusValues = getAndConvertStatusValues();

    // do antal lektioner things
    const updatedLektionerSettings =
      eventType === "lektioner"
        ? settings?.antalLektioner.filter((set) => set.key !== key)
        : settings?.antalLektioner;

    // do modtaget dato things
    const updatedRegDatoSettings =
      eventType === "modtaget"
        ? settings?.modtagetDatoer.filter((set) => set.key !== key)
        : settings?.modtagetDatoer;

    // do slut dato thigns
    const updatedEndDateSettings =
      eventType === "slutDato"
        ? settings?.slutDatoer.filter((set) => set.key !== key)
        : settings?.slutDatoer;

    const updatedSettings: IPersonEfteruddannelseFilterSetting = {
      status: statusValues,
      antalLektioner: updatedLektionerSettings,
      modtagetDatoer: updatedRegDatoSettings,
      slutDatoer: updatedEndDateSettings,
    };

    const canBuildFilter =
      updatedSettings.antalLektioner.length ||
      updatedSettings.modtagetDatoer.length ||
      updatedSettings.slutDatoer.length ||
      updatedSettings.status.length;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    // update the query filter
    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonEfteruddannelseFilterSetting
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    if (queryFilter.filter === "") {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    } else {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Efteruddannelse"
      value="efteruddannelse"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      <h4 className="h6 mb-1 text-secondary">Slutdato</h4>
      <div className="c-applied-filter-group">
        {settings?.slutDatoer.map(
          (set: IPersonEfteruddannelseFilterSetting["slutDatoer"][number]) => {
            return (
              <AppliedFilterItem
                key={set.key}
                labelPrefix={set.selectedQueryName}
                label={set.displayName}
                removeFilterHandler={() => {
                  removeFilterHandler(set.key, "slutDato");
                }}
              />
            );
          },
        )}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedEndDateQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsEndDateBetween(true);
              return;
            } else {
              setIsEndDateBetween(false);
            }

            const query = dateQueries.find((q) => q.query === value);
            if (query) {
              setSelectedEndDateQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {dateQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedEndDateFrom}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedEndDateFrom(isCompleteDate(date) ? date : null);
          }}
        />
        {isEndDateBetween && (
          <>
            -{" "}
            <DatePicker
              appearance="subtle"
              cleanable={false}
              defaultValue={selectedEndDateTo}
              format="dd-MM-yyyy"
              oneTap
              placement="autoVertical"
              size="xs"
              onChange={(date) => {
                setSelectedEndDateTo(isCompleteDate(date) ? date : null);
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1`}
          disabled={
            !selectedEndDateFrom ||
            (isEndDateBetween &&
              (!selectedEndDateTo || selectedEndDateFrom > selectedEndDateTo))
          }
          onClick={() => {
            updateFilterHandler("endDate");
          }}
        >
          OK
        </button>
      </ComboBox>

      <h4 className="h6 mt-3  mb-1 text-secondary">Status</h4>
      {Object.entries(EfteruddannelseIndberetningStatusId).map(
        ([key, value], index) => {
          let displayName = startCase(
            value.replaceAll("_", " ").toLocaleLowerCase(),
          );

          // æ, ø og å'ify the enum
          displayName = displayName.replaceAll("ae", "æ").replaceAll("oe", "ø");

          return (
            <InputCheckbox
              id={`${value}__${random(0, 1000)}`}
              label={displayName}
              ref={checkboxRefs.current[index]}
              checked={settings?.status.includes(value)}
              onChange={() => updateFilterHandler("status")}
              key={key}
            />
          );
        },
      )}

      <h4 className="h6 mt-3 mb-1 text-secondary">Antal lektioner</h4>

      <div className="c-applied-filter-group">
        {settings?.antalLektioner.map(
          (
            set: IPersonEfteruddannelseFilterSetting["antalLektioner"][number],
          ) => {
            return (
              <AppliedFilterItem
                key={set.key}
                labelPrefix={set.selectedQueryName}
                label={set.displayName}
                removeFilterHandler={() => {
                  removeFilterHandler(set.key, "lektioner");
                }}
              />
            );
          },
        )}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedLektionerQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsLektionerBetween(true);
              return;
            } else {
              setIsLektionerBetween(false);
            }

            const query = numberQueries.find((q) => q.query === value);
            if (query) {
              setSelectedLektionerQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {numberQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <input
          className="form-control"
          type="number"
          placeholder="Antal lektioner"
          ref={inputRef}
          required
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              updateFilterHandler("lessons");
            }
          }}
        />
        {isLektionerBetween && (
          <>
            -{" "}
            <input
              className="form-control"
              type="number"
              placeholder="Antal lektioner"
              ref={inputToRef}
              required
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  updateFilterHandler("lessons");
                }
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            updateFilterHandler("lessons");
          }}
        >
          OK
        </button>
      </ComboBox>

      <h4 className="h6 mt-3 mb-1 text-secondary">Modtaget dato</h4>
      <div className="c-applied-filter-group">
        {settings?.modtagetDatoer.map(
          (
            set: IPersonEfteruddannelseFilterSetting["modtagetDatoer"][number],
          ) => {
            return (
              <AppliedFilterItem
                key={set.key}
                labelPrefix={set.selectedQueryName}
                label={set.displayName}
                removeFilterHandler={() => {
                  removeFilterHandler(set.key, "modtaget");
                }}
              />
            );
          },
        )}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedReceivedDateQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsReceivedDateBetween(true);
              return;
            } else {
              setIsReceivedDateBetween(false);
            }

            const query = dateQueries.find((q) => q.query === value);
            if (query) {
              setSelectedReceivedDateQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {dateQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedReceivedDateFrom}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedReceivedDateFrom(isCompleteDate(date) ? date : null);
          }}
        />
        {isReceivedDateBetween && (
          <>
            -{" "}
            <DatePicker
              appearance="subtle"
              cleanable={false}
              defaultValue={selectedReceivedDateTo}
              format="dd-MM-yyyy"
              oneTap
              placement="autoVertical"
              size="xs"
              onChange={(date) => {
                setSelectedReceivedDateTo(isCompleteDate(date) ? date : null);
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1`}
          disabled={
            !selectedReceivedDateFrom ||
            (isReceivedDateBetween &&
              (!selectedReceivedDateTo ||
                selectedReceivedDateFrom > selectedReceivedDateTo))
          }
          onClick={() => {
            updateFilterHandler("receivedDate");
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
